import { checkParamValue, ParamApply, ParamType, ParamVisibility, type ParamDef, type ParamValue } from '@gr/core/platform/config/registry';
import type { ParamLimitsDto } from '@gr/core/platform/config/dto';

// Ввод значения параметра конфигурации: единица по имени, текст поля, разбор по типу и диапазону.
// Общий для настроек платформы (CFG-UC-2) и личных настроек (PREF-UC-3).

export enum ValueUnit {
  Milliseconds = 'ms',
  Seconds = 'seconds',
  Minutes = 'minutes',
  Hours = 'hours',
  Kilobytes = 'kb',
  Megabytes = 'mb',
  Gigabytes = 'gb',
  Pixels = 'px',
}

const UNIT_SUFFIXES: [string, ValueUnit][] = [
  ['Ms', ValueUnit.Milliseconds],
  ['Seconds', ValueUnit.Seconds],
  ['Minutes', ValueUnit.Minutes],
  ['Hours', ValueUnit.Hours],
  ['Kb', ValueUnit.Kilobytes],
  ['Mb', ValueUnit.Megabytes],
  ['Gb', ValueUnit.Gigabytes],
  ['Px', ValueUnit.Pixels],
];

/** Единица — в имени параметра (ADR-013): reconnectDelaysMs, maxUploadMb. */
export function unitOf(key: string): ValueUnit | null {
  const name = key.split('.').at(-1)!;
  return UNIT_SUFFIXES.find(([suffix]) => name.endsWith(suffix))?.[1] ?? null;
}

/** Текст значения для поля ввода: список — через запятую. Логическое поле — флажок, не текст. */
export function inputText(value: ParamValue | null): string {
  if (value === null) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/** Для проверки значения нужны только тип и границы; остальные поля определения ей не важны. */
function limitsOf(limits: ParamLimitsDto): ParamDef {
  return {
    type: limits.type,
    default: '',
    min: limits.min ?? undefined,
    max: limits.max ?? undefined,
    values: limits.values ?? undefined,
    minItems: limits.minItems ?? undefined,
    maxItems: limits.maxItems ?? undefined,
    visibility: ParamVisibility.Public,
    apply: ParamApply.Live,
    editable: true,
    personal: false,
    primary: false,
    secret: false,
    env: null,
    about: limits.description,
  };
}

/** Ввод администратора или пользователя → значение параметра; null — недопустимо (тип, диапазон, CFG-UC-2.CC-4, PREF-UC-3.CC-1). */
export function parseSettingInput(setting: ParamLimitsDto, input: string | boolean): ParamValue | null {
  const def = limitsOf(setting);
  if (setting.type === ParamType.Boolean) return typeof input === 'boolean' ? input : null;
  const text = String(input).trim();
  switch (setting.type) {
    case ParamType.Integer:
    case ParamType.Number:
      return text === '' ? null : checkParamValue(def, Number(text.replace(',', '.')));
    case ParamType.IntegerList:
      return checkParamValue(
        def,
        text
          .split(/[,;\s]+/)
          .filter(Boolean)
          .map(Number),
      );
    default:
      return checkParamValue(def, setting.type === ParamType.String ? String(input) : text);
  }
}
